'use client';


import { ObjectTreeNode } from './objectTree';

import type { IObjectTreeNode } from './objectTree';
import type * as ENGINE from '@gnsx/genesys.js';

import * as NameHelpers from '@/editor/core/nameHelpers';
import { getDirectChildSceneComponents } from '@/editor/core/objectHelpers';

/** Builds object tree hierarchy nodes for an editable object from an actor or scene component */
export function buildObjectTree(
  addNodeFn: (node: IObjectTreeNode, parentId?: string) => IObjectTreeNode,
  objectId: string,
  objectName: string,
  actor?: ENGINE.Actor,
  rootComp?: ENGINE.SceneComponent,
  debugLog: boolean = false,
) {
  // Track processed components by UUID to prevent cycles
  const processedComps = new Set<string>();

  const rootId = objectId;
  if (debugLog) console.log(`Adding root node with ID: ${rootId}, name: ${objectName}`);

  if (!actor && !rootComp) {
    console.warn('[buildObjectHierarchy] No actor provided');
    return null;
  }

  // Create the root object node.
  const rootObject = actor ?? rootComp!;
  const rootNode = addNodeFn(ObjectTreeNode.create({
    id: rootId,
    name: objectName,
  }));
  rootNode.init(rootObject);

  // Actors have root component node.
  let rootCompNode: IObjectTreeNode | undefined;
  if (actor && rootComp) {
    const id = rootComp.uuid;
    const name = NameHelpers.getComponentDisplayName(rootComp) ?? rootComp.name;
    processedComps.add(id);
    if (debugLog) console.log(`Adding root component node with ID: ${id}, name: ${name}`);
    rootCompNode = addNodeFn(ObjectTreeNode.create({ id, name }), rootNode.id);
    rootCompNode.init(rootComp);
  }

  // Build a queue-based breadth-first traversal instead of recursive. This avoids call stack issues with deeply nested components
  if (rootComp && rootCompNode) {
    const owningActor = actor ?? rootComp.getActor() ?? undefined;
    const queue: { node: IObjectTreeNode, component: ENGINE.SceneComponent }[] = [
      { node: rootCompNode, component: rootComp }
    ];

    while (queue.length > 0) {
      const { node, component } = queue.shift()!;

      const childSceneComps = getDirectChildSceneComponents(component);
      for (const sceneComp of childSceneComps) {
        if (owningActor && sceneComp.getActor() !== owningActor) {
          continue;
        }

        const id = sceneComp.uuid;
        const name = NameHelpers.getComponentDisplayName(sceneComp) ?? sceneComp.name;

        if (processedComps.has(id)) continue;
        processedComps.add(id);

        if (debugLog) console.log(`Adding child component node with ID: ${id}, name: ${name}, parent: ${node.id}`);
        const childNode = addNodeFn(ObjectTreeNode.create({ id, name }), node.id);
        childNode.init(sceneComp);

        queue.push({ node: childNode, component: sceneComp });
      }
    }
  }

  return rootNode;
}
